import { useState } from "react";
import { entidadDelPais } from "../config/integracionPorPais";
import { tiendaDelNumero } from "../utils/numeroDeCajaDeBC";
import dayjs from "dayjs";
import db from "../db/database";
import {
  fetchInvoiceHistory,
  fetchFromBusinessCentral,
  fetchFromBusinessCentralPos,
} from "../utils/businessCentralUtils";
import {
  buildFilters,
  buildSyncMessage,
  mapInvoice,
  mapPostedInvoice,
  mapLine,
  mapPaymentDetails,
  mapLineCalculateVat,
  contextoParaMapearLineas,
  fetchLinesInChunks,
  filterEmptyDocuments,
  descartarPagosDeOtroDocumento,
  delTurno,
} from "../utils/syncUtils";
import { refreshFinalDocumentNumbers } from "../services/invoiceSyncService";
import { numerosDeLosBorrados, planDeReemplazo } from "../services/reemplazoDeHistoricos";
import { getAperturaActiva } from "../services/cajaEstadoService";
import formaDePagoService from "../services/formaDePagoService";
import { devolverElDescuentoAGlobal } from "../utils/descuentoGlobalDeLineas";
import { pullAperturasFromBC } from "../services/aperturaDeCajaSyncService";
import { resolveMoneyDecimals, subtractMoney } from "../utils/money";
import { corteDelAmbienteActivo } from "../utils/corteDeHoraDeBC";
import { apartarSellos } from "../services/selloDeDocumentoBorrado";
import { refreshInvoiceReturnAvailability } from "../services/invoiceService";
import { refrescarElTimbradoDe } from "../services/invoiceSyncService";
import { anotarAviso, anotarPaso } from "../services/syncLog";

export function useSyncHistoricos(handleHistoricosSynced, country) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchAndMapInvoices = async (values) => {
    // No contabilizadas
    let filter = buildFilters(values, false, country?.code);
    const invoiceHistory = await fetchInvoiceHistory(filter);

    // Contabilizadas
    filter = buildFilters(values, true, country?.code);
    // La misma regla que usa la recuperación del número definitivo, y ahora el
    // mismo código: Honduras y El Salvador leen la factura registrada por su
    // propia API, y el abono registrado sigue por OData en los nueve países.
    const { endpoint: postedSalesInvoice, apiV2: isApiV2 } = entidadDelPais(
      country?.code,
      "facturasRegistradas"
    );
    const postedInvoiceHistoryCrudo = await fetchFromBusinessCentral(
      `${postedSalesInvoice}?${filter}`,
      "",
      isApiV2
    );

    // Los sellos de documento borrado salen acá, antes de mapear y antes de
    // pedir líneas y pagos. La colocación no es indiferente: los pagos
    // huérfanos anteriores a este acuerdo no traen `Documento_Definitivo`, así
    // que `descartarPagosDeOtroDocumento` los conserva por su primera rama pase
    // lo que pase. Si el sello se quitara después, su dinero ya habría entrado.
    const { documentos: postedInvoiceHistory, sellos } = apartarSellos(postedInvoiceHistoryCrudo);
    if (sellos.length) {
      anotarAviso(
        "facturas",
        `${sellos.length} documento(s) borrado(s) omitidos al traer históricos: ${sellos
          .map((s) => s.No)
          .join(", ")}`
      );
    }

    // Map
    // El corte del huso: los documentos anteriores llevan la hora en UTC y hay
    // que traerlos a hora local o caen en el turno siguiente.
    const corte = await corteDelAmbienteActivo();
    const mappedInvoices = invoiceHistory.map((obj) => mapInvoice(obj, country?.code, corte));
    // El turno de las registradas se filtra acá y no en el `$filter`: ver
    // `buildFilters`.
    const mappedPostedInvoices = delTurno(
      postedInvoiceHistory.map((obj) => mapPostedInvoice(obj, "Invoice", country?.code, corte)),
      values.Turno,
      "facturas"
    );

    // Lines
    const allLines = [];
    const paymentLines = [];

    // Las tasas de IVA y la cuenta del ICBPER, para lo que las líneas no traen.
    // Ver `contextoParaMapearLineas`.
    const contexto = await contextoParaMapearLineas();

    if (mappedInvoices.length > 0) {
      const invoiceLines = await fetchLinesInChunks(
        "LinesSalesInvoice",
        mappedInvoices,
        (l) => mapLineCalculateVat(l, contexto),
        35,
        "Document_No",
        true
      );

      allLines.push(...invoiceLines);

      const invoicePayments = await fetchLinesInChunks(
        "DetallePagoFactura",
        mappedInvoices,
        mapPaymentDetails,
        35,
        "Document_No_",
        true
      );
      paymentLines.push(...invoicePayments);
    }

    if (mappedPostedInvoices.length > 0) {
      const postedInvoiceLines = await fetchLinesInChunks(
        "LinesPostedSalesInvoice",
        mappedPostedInvoices,
        (l) => mapLine(l, contexto)
      );
      allLines.push(...postedInvoiceLines);

      const postedInvoicePayments = await fetchLinesInChunks(
        "DetallePagoFactura",
        mappedPostedInvoices,
        mapPaymentDetails,
        35,
        "Document_No_",
        true
      );
      // El número de borrador con el que se piden no identifica una sola
      // factura contabilizada -- ver descartarPagosDeOtroDocumento.
      paymentLines.push(
        ...descartarPagosDeOtroDocumento(postedInvoicePayments, mappedPostedInvoices)
      );
    }

    return { mappedInvoices, mappedPostedInvoices, allLines, paymentLines };
  };

  const fetchAndMapCreditMemos = async (values) => {
    // No contabilizadas
    let filter = buildFilters(values, false, country?.code);
    const creditMemoHistoryCrudo = await fetchFromBusinessCentral(`SalesCreditMemo?${filter}`);

    // Business Central puede contestar un «Error: ...» dentro de un HTTP 200, y
    // entonces la lectura vuelve como cadena vacía en vez de como arreglo (ver
    // `checkValueForErrors`). Sin esta normalización, el `.map` de más abajo
    // reventaba con un «creditMemoHistory.map is not a function»: un mensaje que
    // no nombra ni a Business Central ni a la entidad, y que el cajero veía tal
    // cual en pantalla mientras el motivo real se quedaba en la consola.
    //
    // El camino de las registradas no falla, pero no sirve de modelo: aguanta
    // de casualidad, porque `apartarSellos` recorre con `for...of` y sobre una
    // cadena vacía itera cero veces. Con una cadena NO vacía recorrería letra
    // por letra.
    const creditMemoHistory = Array.isArray(creditMemoHistoryCrudo) ? creditMemoHistoryCrudo : [];
    if (!Array.isArray(creditMemoHistoryCrudo)) {
      anotarAviso(
        "notas de crédito",
        "Business Central contestó con un error dentro de un 200 al pedir SalesCreditMemo: no se trae ninguna nota de crédito sin contabilizar"
      );
    }

    // Contabilizadas
    filter = buildFilters(values, true, country?.code);
    const postedCreditMemoHistoryCrudo = await fetchFromBusinessCentral(
      `PostedSalesCreditMemo?${filter}`
    );

    // Mismo apartado que en facturas, y por el mismo motivo: entre los 30 pagos
    // huérfanos medidos en Ecuador hay notas de crédito --las series `NCY6` y
    // `NCMDSC3`--, así que el camino no es exclusivo de las facturas.
    const { documentos: postedCreditMemoHistory, sellos } = apartarSellos(
      postedCreditMemoHistoryCrudo
    );
    if (sellos.length) {
      anotarAviso(
        "notas de crédito",
        `${sellos.length} documento(s) borrado(s) omitidos al traer históricos: ${sellos
          .map((s) => s.No)
          .join(", ")}`
      );
    }

    // Map
    const corte = await corteDelAmbienteActivo();
    const mappedCreditMemos = creditMemoHistory.map((obj) => mapInvoice(obj, country?.code, corte));
    // El turno de las registradas se filtra acá y no en el `$filter`: ver
    // `buildFilters`.
    const mappedPostedCreditMemos = delTurno(
      postedCreditMemoHistory.map((obj) =>
        mapPostedInvoice(obj, "Credit Memo", country?.code, corte)
      ),
      values.Turno,
      "notas de crédito"
    );

    // Lines
    const allLines = [];
    const paymentLines = [];

    const contexto = await contextoParaMapearLineas();

    if (mappedCreditMemos.length > 0) {
      // mapLineCalculateVat, no mapLine: esta entidad publica
      // Retention_VAT_Percent en 0 y no publica VAT_Percent, así que mapLine
      // se queda con ese 0 y toda la nota de crédito pierde el IVA. Medido:
      // NCMDSC3-000000025 vale 229,02 y se mostraba en 217,07. Es la misma
      // forma que tiene LinesSalesInvoice, que ya usa este mapeador.
      const creditMemoLines = await fetchLinesInChunks(
        "LinesSalesCreditMemo",
        mappedCreditMemos,
        (l) => mapLineCalculateVat(l, contexto),
        35,
        "Document_No",
        true
      );
      allLines.push(...creditMemoLines);

      // El ultimo argumento --pedirlos por Pre_Assigned_No-- no estaba, y sin el
      // se pedian por `No`, que en un documento sin contabilizar esta vacio:
      // el filtro salia como `Document_No_ eq ''` y Business Central no
      // devolvia nada. Toda nota de credito sin contabilizar llegaba sin sus
      // pagos, asi que su importe desaparecia del arqueo del cierre --y con el
      // signo negativo que le corresponde, el cuadre daba de mas--.
      //
      // Las otras tres llamadas del pull ya lo pasaban; esta era la unica que
      // no, y por eso no se veia: las facturas si traian sus pagos.
      const creditMemoPayments = await fetchLinesInChunks(
        "DetallePagoFactura",
        mappedCreditMemos,
        mapPaymentDetails,
        35,
        "Document_No_",
        true
      );
      paymentLines.push(...creditMemoPayments);
    }

    if (mappedPostedCreditMemos.length > 0) {
      const postedCreditMemoLines = await fetchLinesInChunks(
        "LinesPostedSalesCreditMemo",
        mappedPostedCreditMemos,
        (l) => mapLineCalculateVat(l, contexto)
      );
      allLines.push(...postedCreditMemoLines);

      const postedCreditMemoPayments = await fetchLinesInChunks(
        "DetallePagoFactura",
        mappedPostedCreditMemos,
        mapPaymentDetails,
        35,
        "Document_No_",
        true
      );
      // Mismo criterio que en las facturas: el número de borrador puede ser de
      // más de una nota de crédito contabilizada.
      paymentLines.push(
        ...descartarPagosDeOtroDocumento(postedCreditMemoPayments, mappedPostedCreditMemos)
      );
    }

    return { mappedCreditMemos, mappedPostedCreditMemos, allLines, paymentLines };
  };

  // Trae los cierres de caja (ShiftsClosing) ya calculados en Business Central
  // para la caja/rango de fechas consultado. A diferencia de facturas/notas de
  // crédito (que se reemplazan por completo en cada sincronización), acá se
  // agrega de forma aditiva: db.cierreDeCaja también contiene cierres locales
  // pendientes de sincronizar, y un .clear() los destruiría.
  const fetchAndMapCierresDeCaja = async (values) => {
    const start = dayjs(values.FechaInicio).format("YYYY-MM-DD");
    const end = dayjs(values.FechaFin).format("YYYY-MM-DD");

    const filters = [
      `RegisterID eq '${values.Caja}'`,
      `(PostingDate ge ${start} and PostingDate le ${end})`,
    ];
    if (values.Turno) {
      filters.push(`ShiftNo eq ${values.Turno}`);
    }

    const closings = await fetchFromBusinessCentralPos(
      "ShiftsClosing",
      filters.join(" and "),
      "ShiftsClosingLines($expand=ShiftsClosingLinesDetails)"
    );

    if (!closings.length) {
      return { nuevosCierres: [], cierresEncontrados: 0 };
    }

    const paymentForms = await formaDePagoService.getAllFormasDePago();
    const nombrePorCodigo = new Map(paymentForms.map((p) => [p.Code, p.Description]));

    const cierresLocales = await db.cierreDeCaja.toArray();
    const existingNos = new Set(cierresLocales.map((c) => c.No).filter(Boolean));

    // Un cierre local recién cerrado todavía no tiene el No de BC (sigue con su
    // correlativo local) hasta que termine de sincronizarse -- comparar solo por
    // No dejaría pasar un duplicado. Mientras tanto, se identifica por Caja+Fecha
    // (el dato local no guarda Turno en el cierre, así que no se puede afinar más).
    const pendingKeys = new Set(
      cierresLocales
        .filter((c) => c.pendingSync === 1 && c.Estado === "Cerrado" && c.FechaDeCierre)
        .map((c) => `${c.Caja}|${dayjs(c.FechaDeCierre).format("YYYY-MM-DD")}`)
    );

    const now = new Date();
    const nuevosCierres = [];

    // Los cierres que un pull anterior guardó sin fecha --se leía `PostingDate`,
    // que la API no publica-- se completan aquí. Sin esto, como su número ya
    // está, se saltarían para siempre y seguirían fuera de los informes.
    const sinFechaPorNo = new Map(
      cierresLocales.filter((c) => c.No && !c.FechaDeCierre).map((c) => [c.No, c])
    );
    for (const closing of closings) {
      const local = sinFechaPorNo.get(closing.ShiftClosingNo);
      const fecha = fechaDelCierreDeBC(closing);
      if (local?.id != null && fecha) {
        await db.cierreDeCaja.update(local.id, { FechaDeCierre: fecha, lastSynced: now });
      }
    }

    closings.forEach((closing) => {
      const no = closing.ShiftClosingNo || "";
      if (!no || existingNos.has(no)) return;

      const fechaDelCierre = fechaDelCierreDeBC(closing);
      const pendingKey = `${closing.RegisterID}|${dayjs(fechaDelCierre).format("YYYY-MM-DD")}`;
      if (pendingKeys.has(pendingKey)) return;

      existingNos.add(no);

      const tienda = tiendaDelNumero(no) || String(values.TPV ?? "").trim();
      const lines = closing.shiftsClosingLines || closing.ShiftsClosingLines || [];
      // Una línea por forma de pago: es la clave de la tabla en BC.
      const cuadreMetodosPago = mapearLineasDeCierre(lines, nombrePorCodigo);

      nuevosCierres.push({
        No: no,
        Cajero: closing.UserId || "",
        TPV: tienda,
        Caja: closing.RegisterID || values.Caja,
        Turno: closing.ShiftNo || "",
        FechaDeCierre: fechaDelCierre,
        Estado: "Cerrado",
        ImporteDeCierreUS: closing.AmountTotal ?? 0,
        CuadreMetodosPago: JSON.stringify(cuadreMetodosPago),
        idApertura: null,
        pendingSync: 0,
        lastModified: now,
        lastSynced: now,
        deleted: false,
      });
    });

    return { nuevosCierres, cierresEncontrados: closings.length };
  };

  // No se pueden mezclar cajas: un cierre ya cerrado que no pertenezca a la caja
  // usada en este Traer Histórico ni a la caja actualmente abierta (si la hay) no
  // debe quedar conviviendo localmente -- se elimina.
  const pruneCierresDeOtraCaja = async (values) => {
    const aperturaActiva = await getAperturaActiva();
    const cajasValidas = new Set([values.Caja, aperturaActiva?.Caja].filter(Boolean));

    await db.cierreDeCaja
      .filter((c) => c.Estado === "Cerrado" && !cajasValidas.has(c.Caja))
      .delete();
  };

  // Mismo criterio que pruneCierresDeOtraCaja, pero para aperturas de caja
  // ya traídas por histórico (con No asignado) -- no se tocan las
  // pendientes de sincronizar ni la apertura actualmente activa.
  const pruneAperturasDeOtraCaja = async (values) => {
    const aperturaActiva = await getAperturaActiva();
    const cajasValidas = new Set([values.Caja, aperturaActiva?.Caja].filter(Boolean));

    await db.aperturaDeCaja.filter((a) => !!a.No && !cajasValidas.has(a.Caja)).delete();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Fetch data
      const {
        mappedInvoices,
        mappedPostedInvoices,
        allLines: invoiceLines,
        paymentLines: invoicePayments,
      } = await fetchAndMapInvoices(values);

      const {
        mappedCreditMemos,
        mappedPostedCreditMemos,
        allLines: creditMemoLines,
        paymentLines: creditMemoPayments,
      } = await fetchAndMapCreditMemos(values);

      // El descuento por forma de pago se devuelve a global antes de guardar.
      //
      // El terminal lo aplica una vez sobre el documento; Business Central lo
      // guarda repartido por línea. Sin deshacer ese reparto, un documento que
      // salió de acá con un 4% de efectivo vuelve como un documento con 4% en
      // cada línea -- y el descuento de línea anula al de forma de pago
      // (decisión D5), así que la factura aparecía sin descuento en el detalle
      // mientras el ticket sí lo enseñaba, y con un céntimo de diferencia entre
      // el total y lo cobrado. Ver utils/descuentoGlobalDeLineas.js.
      const formasDePago = await formaDePagoService.getAllFormasDePago();
      const dtoPorCodigo = new Map(
        (formasDePago || []).map((f) => [(f?.Code || "").trim().toUpperCase(), f?.Dto || 0])
      );
      const dtoDe = (codigo) => dtoPorCodigo.get((codigo || "").trim().toUpperCase()) || 0;

      const globalesEnFacturas = devolverElDescuentoAGlobal({
        lines: invoiceLines,
        payments: invoicePayments,
        dtoDe,
        country,
      });
      const globalesEnNotas = devolverElDescuentoAGlobal({
        lines: creditMemoLines,
        payments: creditMemoPayments,
        dtoDe,
        country,
      });

      const reconocidos = globalesEnFacturas.documentos.length + globalesEnNotas.documentos.length;
      if (reconocidos > 0) {
        anotarPaso(
          "facturas",
          `${reconocidos} documento(s) con descuento por forma de pago devuelto a global`
        );
      }

      const keys = {
        getLineKey: (d) => (d.posted === 1 ? d.No : d.Pre_Assigned_No),
        getPaymentKey: (d) => d.Pre_Assigned_No || d.No,
      };
      const inv = filterEmptyDocuments({
        documents: [...mappedInvoices, ...mappedPostedInvoices],
        lines: globalesEnFacturas.lines,
        payments: globalesEnFacturas.payments,
        ...keys,
      });
      const nc = filterEmptyDocuments({
        documents: [...mappedCreditMemos, ...mappedPostedCreditMemos],
        lines: globalesEnNotas.lines,
        payments: globalesEnNotas.payments,
        ...keys,
      });

      // Reemplazo acotado, no `clear()` de la tabla entera.
      //
      // El `clear()` borraba dos cosas que no se pidieron: los documentos de
      // otras cajas y fechas, y --peor-- los que el terminal todavia le debe a
      // Business Central. Perder uno de esos pierde su `Id_TPV`, que es con lo
      // que el guardia de idempotencia reconoce la venta al reintentar; sin el,
      // BC recibe un documento que no puede emparejar y lo crea otra vez.
      // Acuerdo `contrato-traer-historicos.md`, ronda 3.
      const alcance = {
        caja: values.Caja,
        desde: dayjs(values.FechaInicio).format("YYYY-MM-DD"),
        hasta: dayjs(values.FechaFin).format("YYYY-MM-DD"),
        turno: values.Turno,
      };
      const existentes = await db.invoices.toArray();
      const { aBorrar, aInsertar } = planDeReemplazo({
        existentes,
        entrantes: [...inv.documents, ...nc.documents],
        alcance,
      });
      const numerosRetirados = numerosDeLosBorrados(existentes, aBorrar);

      await db.invoices.bulkDelete(aBorrar);
      await db.invoices.bulkAdd(aInsertar);

      // Las lineas y los pagos siguen a su documento: se retiran los de los
      // borrados y solo esos, o una venta conservada se queda sin detalle.
      const deUnBorrado = (fila) =>
        numerosRetirados.has(String(fila?.invoiceNo ?? "")) ||
        numerosRetirados.has(String(fila?.Document_No_ ?? ""));

      const lineasViejas = await db.invoiceLines.toArray();
      await db.invoiceLines.bulkDelete(lineasViejas.filter(deUnBorrado).map((l) => l.id));
      await db.invoiceLines.bulkAdd([...inv.lines, ...nc.lines]);

      const pagosViejos = await db.invoicePaymentLines.toArray();
      await db.invoicePaymentLines.bulkDelete(pagosViejos.filter(deUnBorrado).map((p) => p.id));
      await db.invoicePaymentLines.bulkAdd([...inv.payments, ...nc.payments]);

      // Cierres de caja: aditivo, no reemplaza lo existente (ver comentario en fetchAndMapCierresDeCaja)
      await pruneCierresDeOtraCaja(values);
      const { nuevosCierres, cierresEncontrados } = await fetchAndMapCierresDeCaja(values);
      if (nuevosCierres.length) {
        await db.cierreDeCaja.bulkAdd(nuevosCierres);
      }

      // Aperturas de caja: mismo patrón aditivo que cierres de caja
      const start = dayjs(values.FechaInicio).format("YYYY-MM-DD");
      const end = dayjs(values.FechaFin).format("YYYY-MM-DD");
      await pruneAperturasDeOtraCaja(values);
      const { nuevas: nuevasAperturas, encontradas: aperturasEncontradas } =
        await pullAperturasFromBC({
          fechaInicio: start,
          fechaFin: end,
          caja: values.Caja,
          turno: values.Turno,
        });

      // Recalcular qué facturas quedaron sin nada que devolver.
      //
      // `fullyReturned` es lo que mira el listado para deshabilitar el icono de
      // «Crear nota de crédito», y sólo se recalculaba al crear, guardar o
      // borrar un abono --nunca al traer históricos--. Así que una factura
      // traída ya abonada por completo llegaba sin ese campo: el icono se veía
      // activo, el cajero pulsaba, y sólo entonces se le decía que no quedaba
      // nada. El listado y la pantalla se contradecían porque uno leía un campo
      // guardado y la otra calculaba en vivo.
      //
      // Se recalcula sólo sobre las facturas que este pull trajo, no sobre toda
      // la tabla.
      const facturasTraidas = inv.documents.map((d) => d.No || d.Pre_Assigned_No).filter(Boolean);
      for (const numero of new Set(facturasTraidas)) {
        await refreshInvoiceReturnAvailability(numero);
      }

      // Y el estado del timbrado, por el mismo motivo que arriba.
      //
      // El pull **no trae nada del sello**: `Clave`, `Electronic_Document_Status`
      // y `Mensaje` no están en el mapeo. Y como dentro del alcance las filas se
      // borran y se reemplazan por la versión de Business Central, un Traer
      // Históricos se llevaba por delante lo que ya se supiera: después de
      // traer los documentos de hoy, el distintivo de timbrado desaparecía.
      //
      // Se preguntan sólo los que quedaron sin clave, así que un segundo TH
      // seguido no vuelve a consultar los que ya se resolvieron.
      const traidos = await db.invoices
        .where("No")
        .anyOf([...new Set([...inv.documents, ...nc.documents].map((d) => d.No).filter(Boolean))])
        .toArray()
        .catch(() => []);
      const timbrado = await refrescarElTimbradoDe(traidos, country?.code);

      const count = (docs, p) => docs.filter((d) => d.posted === p).length;
      setSuccess(
        buildSyncMessage(
          count(inv.documents, 0),
          count(nc.documents, 0),
          count(inv.documents, 1),
          count(nc.documents, 1),
          nuevosCierres.length,
          cierresEncontrados,
          nuevasAperturas,
          aperturasEncontradas
        ) +
          // Sólo si hubo algo que mirar: en los países sin comprobante
          // electrónico esta línea no significaría nada.
          (timbrado.revisados
            ? `
Timbrados recuperados: ${timbrado.timbrados} de ${timbrado.revisados} revisados.` +
              (timbrado.sinRevisar
                ? ` Quedan ${timbrado.sinRevisar} por revisar: vuelve a traerlos o reimprímelos.`
                : "")
            : "")
      );

      if (handleHistoricosSynced) {
        await handleHistoricosSynced();
      }

      return true;
    } catch (error) {
      setError(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshCorrelatives = async (values) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const refreshResult = await refreshFinalDocumentNumbers(country, values);
      // El timbrado se cuenta aparte y sólo si hubo algo que mirar: en los países
      // sin comprobante electrónico ese recuento no significa nada.
      const delTimbrado = refreshResult.timbradosRevisados
        ? ` Timbrados recuperados: ${refreshResult.timbrados} de ${refreshResult.timbradosRevisados} revisados.`
        : "";
      const message = `Actualización de correlativos completada. Revisadas: ${refreshResult.totalCandidates}, actualizadas: ${refreshResult.updated}, sin número final: ${refreshResult.notFound}.${delTimbrado}`;
      setSuccess(message);

      if (handleHistoricosSynced) {
        await handleHistoricosSynced();
      }
    } catch (error) {
      setError(error.message || "No se pudieron actualizar los correlativos.");
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    setLoading,
    error,
    success,
    setError,
    setSuccess,
    handleSubmit,
    handleRefreshCorrelatives,
  };
}

/**
 * El cuadre por forma de pago de un cierre traído del histórico.
 *
 * Una línea por forma de pago, que es la clave de la tabla en BC.
 *
 * Vive fuera del gancho y exportado para poder probarlo: acá se decidió mal una
 * vez y no se notó. La página de API expone el importe digitado como
 * `AmountEntered`, **sin guion bajo**, y se leía con él. Venía siempre
 * indefinido, así que todo cierre importado mostraba digitado 0 y una
 * diferencia igual a menos todo lo cobrado. Nada falló: los números estaban
 * ahí, sólo que eran otros.
 */
/**
 * Los códigos que el cajero anotó por forma de pago al cerrar.
 *
 * El formulario los guarda como `[{codigo, total}]` y el envío los serializa a
 * `[{Code, Amount}]` en el campo `Detail` de la línea (ver
 * buildShiftsClosingLines). Antes se descartaban al traer el cierre --se fijaba
 * `detalle: []`-- así que un cierre importado perdía el desglose que sí tiene
 * uno creado localmente: los números de voucher, de ticket POS o de
 * referencia bancaria quedaban sin rastro.
 *
 * Se tolera cualquier cosa en el campo: viene de BC como texto y un JSON roto
 * no debe tumbar la importación de un cierre entero.
 */
export const parsearDetalleDeLinea = (detail) => {
  if (!detail) return [];
  if (Array.isArray(detail)) return detail;

  try {
    const filas = JSON.parse(detail);
    if (!Array.isArray(filas)) return [];

    return filas.map((fila) => ({
      codigo: fila?.Code ?? fila?.codigo ?? "",
      total: Number(fila?.Amount ?? fila?.total) || 0,
    }));
  } catch {
    return [];
  }
};

/**
 * La fecha de un cierre de `ShiftsClosing`.
 *
 * La API de Punto de Venta la publica como **`postingDate`**, en minúscula, al
 * lado de `ShiftClosingNo`, `ShiftNo` y `RegisterID` con mayúscula --y
 * `initialAmount` también en minúscula--. Se leía `PostingDate`, que no existe:
 * todo cierre llegaba sin fecha, «Cierre de caja por tienda» los descartaba
 * todos y decía «No se encontraron datos», y «Traer históricos» los guardaba sin
 * fecha. Medido en Perú TEST el 27/10/2026: 16 cierres en BC, ninguno en el
 * informe. El filtro `$filter=PostingDate ge …` sí funciona, porque Business
 * Central no distingue mayúsculas en el filtro; sólo en la respuesta.
 *
 * Se lee primero la que publica hoy y, de respaldo, la otra, por si una versión
 * de la API la cambia.
 */
export const fechaDelCierreDeBC = (closing) => closing?.postingDate || closing?.PostingDate || "";

export const mapearLineasDeCierre = (lines = [], nombrePorCodigo = new Map(), country = null) => {
  const decimals = resolveMoneyDecimals(country);

  return lines.map((line) => {
    const cobro = line.Amount || 0;
    const digitado = line.AmountEntered ?? line.Amount_Entered ?? 0;

    return {
      codigoMetodoPago: line.PaymentMethod,
      nombre: nombrePorCodigo.get(line.PaymentMethod) || line.PaymentMethod,
      cobro,
      digitado,
      // Se calcula, no se lee: Business Central deriva la diferencia de estos
      // dos campos y no la publica en la página de API. Es la misma resta que
      // hace el formulario de cierre.
      //
      // Con subtractMoney y no con un menos pelado: los dos importes vienen
      // redondeados a centavos y restarlos en coma flotante deriva
      // (1234,56 - 1000,01 da 234,54999999999995). El formato tapa el número,
      // pero el color de la celda compara contra cero en crudo y pinta de rojo
      // un arqueo que cuadra.
      diferencia: subtractMoney(digitado, cobro, decimals),
      detalle: parsearDetalleDeLinea(line.Detail),
    };
  });
};
